import { useParams, Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from '../config/api'

interface ApiProjectDetail {
  id: string
  title: string
  description: string
  image: string
  category?: string
  status?: string
  technologies: string[]
  features: string[]
  liveUrl?: string
  githubUrl?: string
  owner?: string
  challenges?: string[]
  solutions?: string[]
  results?: string[]
  type?: 'business' | 'personal'

  // Optional legacy/extended fields (some older static content used these)
  inspiration?: string
  personalTouches?: string[]
  learnings?: string[]
  futureEnhancements?: string[]
}

const isImageUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const str = value.trim()
  if (!str) return false
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/') || str.includes('.')
}

export default function PersonalProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const [project, setProject] = useState<ApiProjectDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('overview')

  useEffect(() => {
    let isMounted = true
    const fetchProject = async () => {
      if (!id) {
        setProject(null)
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const res = await fetch(`${API_BASE}/api/v1/projects/${encodeURIComponent(id)}`)
        if (!res.ok) {
          throw new Error(`Projects API request failed: ${res.status} ${res.statusText}`)
        }
        const json = await res.json()
        const raw = (json?.data ?? json) as Record<string, unknown>

        const normalized: ApiProjectDetail = {
          id: typeof raw.id === 'string' ? raw.id : id,
          title: typeof raw.title === 'string' ? raw.title : 'Untitled Project',
          description: typeof raw.description === 'string' ? raw.description : '',
          image: typeof raw.image === 'string' && raw.image ? raw.image : '📱',
          category: typeof raw.category === 'string' ? raw.category : undefined,
          status: typeof raw.status === 'string' ? raw.status : undefined,
          technologies: Array.isArray(raw.technologies) ? raw.technologies.filter((t): t is string => typeof t === 'string') : [],
          features: Array.isArray(raw.features) ? raw.features.filter((f): f is string => typeof f === 'string') : [],
          liveUrl: typeof raw.liveUrl === 'string' ? raw.liveUrl : undefined,
          githubUrl: typeof raw.githubUrl === 'string' ? raw.githubUrl : undefined,
          owner: typeof raw.owner === 'string' ? raw.owner : undefined,
          challenges: Array.isArray(raw.challenges) ? raw.challenges.filter((c): c is string => typeof c === 'string') : undefined,
          solutions: Array.isArray(raw.solutions) ? raw.solutions.filter((s): s is string => typeof s === 'string') : undefined,
          results: Array.isArray(raw.results) ? raw.results.filter((r): r is string => typeof r === 'string') : undefined,
          type: (raw.type === 'business' || raw.type === 'personal') ? raw.type : undefined,

          inspiration: typeof raw.inspiration === 'string' ? raw.inspiration : undefined,
          personalTouches: Array.isArray(raw.personalTouches) ? raw.personalTouches.filter((t): t is string => typeof t === 'string') : undefined,
          learnings: Array.isArray(raw.learnings) ? raw.learnings.filter((l): l is string => typeof l === 'string') : undefined,
          futureEnhancements: Array.isArray(raw.futureEnhancements) ? raw.futureEnhancements.filter((e): e is string => typeof e === 'string') : undefined,
        }

        if (isMounted) {
          setProject(normalized)
        }
      } catch (e) {
        if (isMounted) {
          setProject(null)
          setError(e instanceof Error ? e.message : 'Failed to load project')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchProject()
    return () => {
      isMounted = false
    }
  }, [id])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center p-8">
          <p className="text-sm font-mono uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center max-w-md mx-auto p-8 rounded-card border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <h1 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
            Project Not Found
          </h1>
          <p className="text-sm mb-8 leading-relaxed" style={{ color: 'var(--muted)' }}>
            {error ? error : "Sorry, we couldn't find the project you're looking for."}
          </p>
          <Link to="/team" className="btn-pill btn-primary no-underline inline-block">
            ← Back to Team
          </Link>
        </div>
      </div>
    )
  }

  const learnings = (project.learnings?.length
    ? project.learnings
    : project.solutions?.length
    ? project.solutions
    : project.results?.length
    ? project.results
    : [])

  const sections = [
    { id: 'overview', label: 'Overview' },
    ...(project.inspiration ? [{ id: 'inspiration', label: 'Inspiration' }] : []),
    ...(project.personalTouches && project.personalTouches.length > 0 ? [{ id: 'personal-touches', label: 'Personal Touches' }] : []),
    ...(project.challenges && project.challenges.length > 0 ? [{ id: 'challenges', label: 'Challenges' }] : []),
    ...(learnings.length > 0 ? [{ id: 'learnings', label: 'Learnings' }] : []),
    ...(project.futureEnhancements && project.futureEnhancements.length > 0 ? [{ id: 'future', label: 'Future' }] : [])
  ]

  return (
    <div className="min-h-screen pt-32 pb-16" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Helmet>
        <title>{project.title} | Ionic Errrrs Code</title>
        <meta name="description" content={project.description.substring(0, 155)} />
        <link rel="canonical" href={`https://ionicerrrrscode.com/projects/${project.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Ionic Errrrs Code" />
        <meta property="og:title" content={`${project.title} | Ionic Errrrs Code`} />
        <meta property="og:description" content={project.description.substring(0, 155)} />
        <meta property="og:url" content={`https://ionicerrrrscode.com/projects/${project.id}`} />
        {project.image && <meta property="og:image" content={project.image} />}
      </Helmet>

      <div className="container-page max-w-5xl px-4">
        {/* Back Button */}
        <div className="mb-10">
          <Link 
            to="/team" 
            className="inline-flex items-center text-sm font-semibold no-underline hover:underline"
            style={{ color: 'var(--text)' }}
          >
            ← Back to Team
          </Link>
        </div>

        {/* Hero Header Section */}
        <div className="flex flex-col lg:flex-row items-center gap-12 pb-12 border-b mb-12" style={{ borderColor: 'var(--border)' }}>
          <div
            className="w-32 h-32 lg:w-40 lg:h-40 rounded-2xl flex-shrink-0 flex items-center justify-center border text-6xl"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
          >
            {isImageUrl(project.image) ? (
              <img 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover rounded-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.nextElementSibling?.classList.remove('hidden')
                }}
              />
            ) : null}
            <span className={`${isImageUrl(project.image) ? 'hidden' : ''}`}>
              {project.image}
            </span>
          </div>

          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
              <span className="badge">
                <span className="badge-dot" style={{ background: 'var(--accent)' }} />
                {project.status ?? 'Active'}
              </span>
              {project.category && (
                <span className="badge">
                  {project.category}
                </span>
              )}
              {project.owner && (
                <span className="badge">
                  👤 Created by {project.owner}
                </span>
              )}
            </div>

            <h1 className="font-display text-4xl lg:text-5xl font-bold mb-4" style={{ letterSpacing: '-0.02em' }}>
              {project.title}
            </h1>
            
            <p className="text-[1.12rem] leading-relaxed mb-6 max-w-2xl mx-auto lg:mx-0" style={{ color: 'var(--muted)' }}>
              {project.description}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4">
              {project.liveUrl && (
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill btn-primary no-underline"
                >
                  View Live Demo
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill btn-ghost no-underline"
                >
                  View Source Code
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b mb-12 flex overflow-x-auto scrollbar-hide gap-8" style={{ borderColor: 'var(--border)' }}>
          {sections.map((tab) => {
            const isActive = activeSection === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className="pb-4 font-semibold text-xs uppercase tracking-wider whitespace-nowrap transition-all duration-300"
                style={{
                  color: isActive ? 'var(--text)' : 'var(--muted)',
                  borderBottom: isActive ? '2px solid var(--text)' : '2px solid transparent',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 0,
                  paddingLeft: 0,
                  paddingRight: 0
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab Contents */}
        <div>
          {activeSection === 'overview' && (
            <div className="space-y-12">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Tech Column */}
                <div className="lg:col-span-1">
                  <div className="card-studio h-full p-8">
                    <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                      Technologies
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span key={tech} className="tag-chip">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Features Column */}
                <div className="lg:col-span-2">
                  <div className="card-studio h-full p-8">
                    <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                      Key Features
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {project.features.map((feature) => (
                        <div
                          key={feature}
                          className="p-4 rounded-xl border flex items-start gap-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                        >
                          <span style={{ color: 'var(--border-strong)', fontWeight: 'bold' }}>◦</span>
                          <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Text overview & Solutions */}
              <div className="card-studio p-8">
                <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                  Project Overview
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--muted)' }}>
                  {project.description}
                </p>
                
                {project.solutions && project.solutions.length > 0 && (
                  <div className="mt-8 pt-8 border-t" style={{ borderColor: 'var(--border)' }}>
                    <h4 className="font-display text-[1.12rem] font-bold mb-4" style={{ color: 'var(--text)' }}>
                      Solutions Implemented
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {project.solutions.map((solution) => (
                        <div
                          key={solution}
                          className="p-4 rounded-xl border flex items-start gap-3"
                          style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                        >
                          <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                            {solution}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'inspiration' && project.inspiration && (
            <div className="card-studio p-8">
              <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Inspiration
              </h3>
              <p className="text-base leading-relaxed" style={{ color: 'var(--muted)' }}>
                {project.inspiration}
              </p>
            </div>
          )}

          {activeSection === 'personal-touches' && project.personalTouches && project.personalTouches.length > 0 && (
            <div className="card-studio p-8">
              <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Personal Touches
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {project.personalTouches.map((touch) => (
                  <div
                    key={touch}
                    className="p-4 rounded-xl border flex items-start gap-3"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                  >
                    <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>✦</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
                      {touch}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'challenges' && project.challenges && project.challenges.length > 0 && (
            <div className="card-studio p-8">
              <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Technical Challenges
              </h3>
              <div className="space-y-4">
                {project.challenges.map((challenge) => (
                  <div
                    key={challenge}
                    className="p-4 rounded-xl border flex items-start gap-3"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                  >
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>!</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'learnings' && learnings.length > 0 && (
            <div className="card-studio p-8">
              <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Key Learnings
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {learnings.map((learning) => (
                  <div
                    key={learning}
                    className="p-4 rounded-xl border flex items-start gap-3"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                  >
                    <span style={{ color: 'var(--border-strong)', fontWeight: 'bold' }}>◦</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{learning}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'future' && project.futureEnhancements && project.futureEnhancements.length > 0 && (
            <div className="card-studio p-8">
              <h3 className="font-display text-xl font-bold mb-6" style={{ color: 'var(--text)' }}>
                Future Enhancements
              </h3>
              <div className="space-y-4">
                {project.futureEnhancements.map((enhancement) => (
                  <div
                    key={enhancement}
                    className="p-4 rounded-xl border flex items-start gap-3"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}
                  >
                    <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>🚀</span>
                    <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{enhancement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA Closing */}
        <div className="text-center mt-12">
          <div className="cta-block" style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
              Interested in collaborating?
            </h3>
            <p className="mt-3 text-[1.06rem]" style={{ color: 'var(--inverse-text)', opacity: 0.6, maxWidth: '500px', margin: '12px auto 0' }}>
              We&apos;re always excited to work on new concepts and turn ideas into beautiful digital products.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <Link to="/contact" className="btn-pill btn-inverse no-underline">
                Start a Conversation
              </Link>
              <Link to="/team" className="btn-pill btn-ghost no-underline" style={{ borderColor: 'var(--inverse-text)', color: 'var(--inverse-text)' }}>
                Meet the Team
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
